import { type Rect, type Vec } from './geometry'

// How a connection is drawn between two things: it leaves and enters on the
// sides that face each other, as a gentle curve. Shared by the canvas and by
// the read-only scenes embedded in the chat, so both draw the same line.

export type Side = 'top' | 'right' | 'bottom' | 'left'

/** Where the segment from `from`'s centre towards `to`'s centre leaves `from`, and on which side. */
export function exitPoint(from: Rect, to: Rect): Vec & { side: Side } {
  const cx = from.x + from.w / 2
  const cy = from.y + from.h / 2
  const dx = to.x + to.w / 2 - cx
  const dy = to.y + to.h / 2 - cy
  if (dx === 0 && dy === 0) return { x: cx, y: from.y, side: 'top' }
  if (Math.abs(dx) * from.h > Math.abs(dy) * from.w) {
    return {
      x: dx > 0 ? from.x + from.w : from.x,
      y: cy + (dy * (from.w / 2)) / Math.abs(dx),
      side: dx > 0 ? 'right' : 'left',
    }
  }
  return {
    x: cx + (dx * (from.h / 2)) / Math.abs(dy),
    y: dy > 0 ? from.y + from.h : from.y,
    side: dy > 0 ? 'bottom' : 'top',
  }
}

/** How far a curve's handle reaches out from `side` (as @xyflow's bezier edges do). */
function handle(p: Vec & { side: Side }, toward: Vec): Vec {
  const reach = (d: number) => (d >= 0 ? 0.5 * d : 0.25 * 25 * Math.sqrt(-d))
  switch (p.side) {
    case 'left':
      return { x: p.x - reach(p.x - toward.x), y: p.y }
    case 'right':
      return { x: p.x + reach(toward.x - p.x), y: p.y }
    case 'top':
      return { x: p.x, y: p.y - reach(p.y - toward.y) }
    case 'bottom':
      return { x: p.x, y: p.y + reach(toward.y - p.y) }
  }
}

/** An SVG path from `a` to `b`, and the point halfway along it (for a label). */
export function edgePath(a: Rect, b: Rect): { d: string; mid: Vec } {
  const s = exitPoint(a, b)
  const t = exitPoint(b, a)
  const c1 = handle(s, t)
  const c2 = handle(t, s)
  // A cubic bezier at its midpoint (t = 0.5).
  const mid = {
    x: 0.125 * s.x + 0.375 * c1.x + 0.375 * c2.x + 0.125 * t.x,
    y: 0.125 * s.y + 0.375 * c1.y + 0.375 * c2.y + 0.125 * t.y,
  }
  return { d: `M${s.x},${s.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${t.x},${t.y}`, mid }
}
